/**
 * ConfirmationDialog Component
 *
 * A confirmation dialog built on Radix UI AlertDialog.
 * Features: title, message, confirm/cancel buttons, danger variant.
 */

import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { AlertTriangle, HelpCircle, Info } from 'lucide-react';

import { cn } from '@/utils/cn';
import { Button, type ButtonProps } from '../Button';

// ============================================
// Types
// ============================================

export interface ConfirmationDialogProps {
  /** Dialog open state */
  open: boolean;
  /** Open state change handler */
  onOpenChange: (open: boolean) => void;
  /** Dialog title */
  title: string;
  /** Dialog message/description */
  message: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Confirm callback */
  onConfirm: () => void;
  /** Cancel callback */
  onCancel?: () => void;
  /** Dialog variant */
  variant?: 'default' | 'danger' | 'warning' | 'info';
  /** Loading state for confirm button */
  isLoading?: boolean;
  /** Show icon */
  showIcon?: boolean;
}

// ============================================
// Variant Configuration
// ============================================

const variantConfig = {
  default: {
    icon: HelpCircle,
    iconClass: 'text-primary',
    iconBgClass: 'bg-primary-100 dark:bg-primary-900/30',
    confirmVariant: 'primary' as ButtonProps['variant'],
  },
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-danger',
    iconBgClass: 'bg-danger-50 dark:bg-danger/20',
    confirmVariant: 'danger' as ButtonProps['variant'],
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-warning',
    iconBgClass: 'bg-warning-50 dark:bg-warning/20',
    confirmVariant: 'primary' as ButtonProps['variant'],
  },
  info: {
    icon: Info,
    iconClass: 'text-primary',
    iconBgClass: 'bg-primary-100 dark:bg-primary-900/30',
    confirmVariant: 'primary' as ButtonProps['variant'],
  },
};

// ============================================
// ConfirmationDialog Component
// ============================================

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
  isLoading = false,
  showIcon = true,
}: ConfirmationDialogProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className="modal-overlay fixed inset-0 z-50 bg-black/50"
        />
        <AlertDialog.Content
          className={cn(
            'modal-content fixed left-[50%] top-[50%] z-50 w-full max-w-md',
            'rounded-lg border shadow-lg',
            'border-border dark:border-border-dark',
            'bg-card dark:bg-card-dark',
            'p-6'
          )}
        >
          <div className="flex gap-4">
            {/* Icon */}
            {showIcon && (
              <div
                className={cn(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full',
                  config.iconBgClass
                )}
              >
                <Icon className={cn('h-5 w-5', config.iconClass)} />
              </div>
            )}

            {/* Content */}
            <div className="flex-1">
              <AlertDialog.Title className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                {title}
              </AlertDialog.Title>
              <AlertDialog.Description className="mt-2 text-sm text-foreground-muted dark:text-foreground-dark-muted">
                {message}
              </AlertDialog.Description>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
                {cancelText}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                variant={config.confirmVariant}
                onClick={handleConfirm}
                loading={isLoading}
              >
                {confirmText}
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

// ============================================
// useConfirmation Hook
// ============================================

import { useState, useCallback } from 'react';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationDialogProps['variant'];
}

interface ConfirmationState extends ConfirmationOptions {
  open: boolean;
  resolve: ((value: boolean) => void) | null;
}

export function useConfirmation() {
  const [state, setState] = useState<ConfirmationState>({
    open: false,
    title: '',
    message: '',
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        ...options,
        open: true,
        resolve,
      });
    });
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && state.resolve) {
      state.resolve(false);
    }
    setState((prev) => ({ ...prev, open }));
  }, [state.resolve]);

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState((prev) => ({ ...prev, open: false }));
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState((prev) => ({ ...prev, open: false }));
  }, [state.resolve]);

  const ConfirmationDialogComponent = useCallback(
    () => (
      <ConfirmationDialog
        open={state.open}
        onOpenChange={handleOpenChange}
        title={state.title}
        message={state.message}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        variant={state.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
    [state, handleOpenChange, handleConfirm, handleCancel]
  );

  return {
    confirm,
    ConfirmationDialog: ConfirmationDialogComponent,
  };
}
